// Excel-style column filter UI (logic in hooks/useColumnFilters.ts):
// • ColumnFilterButton: the small funnel in a column header. Opens a menu
//   with a search box, Select all, one tick box per value (with how many
//   rows have it), and OK / Cancel. Nothing changes until OK.
// • ColumnFilterChips: one chip per filtered column under the header
//   ("Color: D, E, F ×") plus Clear all, so a filter is never forgotten.
//
// The button sits inside a sortable <th>: clicks (also from the portaled
// menu, which React bubbles through the tree) are stopped here so they
// don't sort the column.

import { useMemo, useRef, useState } from 'react';
import { Dropdown } from './ui';
import type { ColumnFilters } from '../hooks/useColumnFilters';

function IconFunnel({ on }: { on: boolean }) {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill={on ? 'currentColor' : 'none'}>
      <path d="M2 3h12l-4.6 5.4V13l-2.8-1.4V8.4L2 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function FilterPanel({ filters, colKey, label }: { filters: ColumnFilters; colKey: string; label: string }) {
  const options = filters.options(colKey);
  const all = options.map(o => o.value);
  const current = filters.selected[colKey];
  const [draft, setDraft] = useState<Set<string>>(() => new Set(current ?? all));
  const [q, setQ] = useState('');

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter(o => o.value.toLowerCase().includes(s)) : options;
  }, [options, q]);
  const shownTicked = shown.filter(o => draft.has(o.value)).length;
  const allShownTicked = shown.length > 0 && shownTicked === shown.length;

  const toggle = (v: string) => setDraft(d => { const n = new Set(d); if (n.has(v)) n.delete(v); else n.add(v); return n; });
  const toggleShown = () => setDraft(d => {
    const n = new Set(d);
    if (allShownTicked) shown.forEach(o => n.delete(o.value)); else shown.forEach(o => n.add(o.value));
    return n;
  });

  const okRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  // Enter = OK, Esc = Cancel. Esc is stopped here so a filter opened inside
  // a popup closes only the filter, not the popup.
  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && draft.size > 0) { e.preventDefault(); okRef.current?.click(); }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelRef.current?.click(); }
  }

  function apply() {
    const picked = all.filter(v => draft.has(v));
    // Everything ticked = no filter on this column.
    filters.set(colKey, picked.length === all.length ? null : picked);
  }

  return (
    <div className="colf-panel" onKeyDown={onKey}>
      <div className="colf-title">Filter: {label}</div>
      <input className="colf-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search" autoFocus />
      <label className="colf-row colf-all">
        <input type="checkbox" checked={allShownTicked}
          ref={el => { if (el) el.indeterminate = shownTicked > 0 && !allShownTicked; }}
          onChange={toggleShown} />
        <span>{q.trim() ? 'Select all shown' : 'Select all'}</span>
      </label>
      <div className="colf-list">
        {shown.length === 0
          ? <div className="colf-empty">No matches</div>
          : shown.map(o => (
            <label key={o.value} className="colf-row">
              <input type="checkbox" checked={draft.has(o.value)} onChange={() => toggle(o.value)} />
              {/* Plain text gets a tooltip; an icon carries its own. */}
              <span className="colf-val" title={typeof filters.show(colKey, o.value) === 'string' ? o.value : undefined}>{filters.show(colKey, o.value)}</span>
              {/* Pick just this one value in one click. */}
              <button type="button" className="colf-only" onClick={e => { e.preventDefault(); setDraft(new Set([o.value])); }}>Only</button>
              <span className="colf-count">{o.count}</span>
            </label>
          ))}
      </div>
      <div className="colf-foot">
        {current && (
          <button type="button" className="colf-link" data-dropdown-close onClick={() => filters.set(colKey, null)}>Clear filter</button>
        )}
        <span style={{ flex: 1 }} />
        <button ref={cancelRef} type="button" className="btn btn-secondary btn-sm" data-dropdown-close>Cancel</button>
        <button ref={okRef} type="button" className="btn btn-primary btn-sm" data-dropdown-close disabled={draft.size === 0} onClick={apply}>OK</button>
      </div>
    </div>
  );
}

export function ColumnFilterButton({ filters, colKey, label, align = 'left' }: {
  filters: ColumnFilters; colKey: string; label: string; align?: 'left' | 'right';
}) {
  const on = !!filters.selected[colKey];
  return (
    <span className={`colf-wrap${on ? ' on' : ''}`} onClick={e => e.stopPropagation()}>
      <Dropdown align={align} closeOnInnerClick={false} trigger={open => (
        <button type="button" className={`colf-btn${on ? ' on' : ''}${open ? ' open' : ''}`} title={on ? `Filtered: ${label}` : `Filter ${label}`} aria-label={`Filter ${label}`}>
          <IconFunnel on={on} />
        </button>
      )}>
        <FilterPanel filters={filters} colKey={colKey} label={label} />
      </Dropdown>
    </span>
  );
}

/** `inline`: sits at the right end of a page's figures row (Items, Total ct…) instead of its own row. */
export function ColumnFilterChips({ filters, inline, hide }: {
  filters: ColumnFilters; inline?: boolean;
  /** Column keys whose chip the page already shows another way (e.g. Bank's
   *  selected account card); with only those active, nothing shows. */
  hide?: string[];
}) {
  const active = hide ? filters.active.filter(f => !hide.includes(f.key)) : filters.active;
  if (active.length === 0) return null;
  return (
    <div className={`colf-chips${inline ? ' colf-chips-inline' : ''} no-print`}>
      {active.map(f => (
        <span key={f.key} className="colf-chip"
          title={f.values.every(v => typeof filters.show(f.key, v) === 'string') ? `${f.label}: ${f.values.join(', ')}` : undefined}>
          <strong>{f.label}:</strong>
          <span className="colf-chip-vals">
            {f.values.slice(0, 3).map((v, i) => (
              <span key={v} className="colf-chip-val">{i > 0 && ', '}{filters.show(f.key, v)}</span>
            ))}
            {f.values.length > 3 && <span className="colf-chip-val">&nbsp;+{f.values.length - 3}</span>}
          </span>
          <button type="button" className="colf-chip-x" onClick={() => filters.set(f.key, null)} aria-label={`Remove ${f.label} filter`}>
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          </button>
        </span>
      ))}
      {/* No Clear all here: the header's red Clear already clears every filter. */}
      <span className="colf-shown">Showing {filters.shown} of {filters.total}</span>
    </div>
  );
}
